/* ==========================================================================
   Reusable Quiz Funnel Engine — Web Components
   --------------------------------------------------------------------------
   Config-driven (window.QUIZ_CONFIG, see quiz.config.js). Nothing brand- or
   campaign-specific lives here. Drop this + quiz.css into any funnel and swap
   the config to reskin it.

   Custom elements:
     <quiz-funnel>    landing intro -> card/list questions -> analyzing -> results
     <quiz-results>   personalized result page

   Light DOM on purpose (global theming + external SDKs can see the markup).
   Questions support two layouts: "cards" (illustration on top, grid) and
   "list" (row buttons). Illustrations are inline SVG line-art from the ICONS
   set below, referenced by key, or a raw <svg> string on the option.

   Analytics: every step is pushed to window.dataLayer (+ gtag/fbq if present)
   and a DOM CustomEvent, so it lands in the same stack the funnel already uses.
   ========================================================================== */

(function () {
  "use strict";

  var SS_KEY = "quizFunnelResult";

  /* ---- inline SVG line-art (simple, unbranded, stroke = currentColor) ----- */
  function svg(inner) {
    return '<svg class="q-illo__svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + "</svg>";
  }
  var ICONS = {
    // hair types
    "type-straight": svg('<line x1="19" y1="38" x2="29" y2="10"/>'),
    "type-wavy": svg('<g transform="rotate(-52 24 24)"><path d="M9 24q4-8 8 0t8 0t8 0"/></g>'),
    "type-curly": svg('<g transform="rotate(-52 24 24)"><path d="M7 24q3-7 6 0t6 0t6 0t6 0t6 0"/></g>'),
    "type-coily": svg('<g transform="rotate(-52 24 24)"><path d="M8 24a3 3 0 1 1 6 0a3 3 0 1 1 6 0a3 3 0 1 1 6 0a3 3 0 1 1 6 0"/></g>'),
    // goals
    "goal-regrow": svg('<path d="M24 40V22"/><path d="M24 27c-6 0-10-4-10-10 8 0 10 4 10 10"/><path d="M24 24c6 0 10-4 10-10-8 0-10 4-10 10"/>'),
    "goal-shed": svg('<path d="M15 11c0 10 4 12 3 22"/><path d="M24 9c0 12 4 15 2 26"/><path d="M33 11c0 10-4 12-3 22"/>'),
    "goal-length": svg('<rect x="18" y="8" width="12" height="32" rx="2"/><line x1="18" y1="16" x2="24" y2="16"/><line x1="18" y1="24" x2="26" y2="24"/><line x1="18" y1="32" x2="24" y2="32"/>'),
    "goal-repair": svg('<path d="M24 38C11 29 13 15 22 17"/><path d="M24 38c13-9 11-23 2-21"/><path d="M20 26l3 3 6-7"/>'),
    // concern areas
    "area-hairline": svg('<path d="M13 34c0-14 22-14 22 0"/><path d="M16 26c3-5 13-5 16 0"/>'),
    "area-crown": svg('<circle cx="24" cy="27" r="11"/><path d="M24 16V8"/><circle cx="24" cy="21" r="2.5"/>'),
    "area-allover": svg('<circle cx="24" cy="24" r="13"/><circle cx="19" cy="20" r="1"/><circle cx="29" cy="21" r="1"/><circle cx="24" cy="29" r="1"/><circle cx="30" cy="28" r="1"/>'),
    "area-ends": svg('<path d="M24 8v22"/><path d="M24 30l-4 10"/><path d="M24 30l4 10"/><path d="M24 30l-1 10"/>'),
    // triggers
    "trig-stress": svg('<path d="M27 8L15 26h9l-3 14 16-22h-9z"/>'),
    "trig-heat": svg('<path d="M24 8c6 8 8 12 6 20-1 5-4 8-6 12-2-4-5-7-6-12-2-8 2-12 6-20z"/>'),
    "trig-hormonal": svg('<path d="M36 20a12 12 0 1 0 1 8"/><path d="M33 11v9h-9"/>'),
    "trig-genetics": svg('<path d="M18 8c0 8 12 8 12 16s-12 8-12 16"/><path d="M30 8c0 8-12 8-12 16s12 8 12 16"/><line x1="20" y1="16" x2="28" y2="16"/><line x1="20" y1="32" x2="28" y2="32"/>'),
    "trig-none": svg('<circle cx="24" cy="24" r="12"/><line x1="17" y1="24" x2="31" y2="24"/>'),
    // benefit tiles (small)
    "ben-growth": svg('<path d="M24 38V13"/><path d="M16 21l8-8 8 8"/>'),
    "ben-stop": svg('<path d="M24 8l13 5v9c0 9-6 14-13 18-7-4-13-9-13-18v-9z"/><path d="M19 24l3 3 7-8"/>'),
    "ben-thicken": svg('<line x1="15" y1="34" x2="15" y2="22"/><line x1="24" y1="34" x2="24" y2="15"/><line x1="33" y1="34" x2="33" y2="26"/>'),
    "ben-baby": svg('<path d="M24 38V26"/><path d="M24 30c-5 0-8-3-8-8 6 0 8 3 8 8"/><circle cx="30" cy="18" r="3"/>'),
    "ben-scalp": svg('<path d="M24 9c6 8 9 12 9 18a9 9 0 0 1-18 0c0-6 3-10 9-18z"/>'),
    "ben-repair": svg('<path d="M24 38C11 29 13 15 24 20c11-5 13 9 0 18z"/>'),
    // gender start
    "start-women": svg('<circle cx="24" cy="17" r="9"/><line x1="24" y1="26" x2="24" y2="40"/><line x1="18" y1="34" x2="30" y2="34"/>'),
    "start-men": svg('<circle cx="20" cy="28" r="9"/><line x1="26" y1="22" x2="36" y2="12"/><path d="M29 12h7v7"/>'),
    // hair length (increasing strand)
    "len-short": svg('<path d="M24 16q5 4 0 8t0 8"/>'),
    "len-medium": svg('<path d="M24 12q5 4 0 8t0 8t0 8"/>'),
    "len-long": svg('<path d="M24 8q5 4 0 8t0 8t0 8t0 8"/>'),
    "len-xlong": svg('<path d="M24 6q5 3.5 0 7t0 7t0 7t0 7t0 6"/>'),
    // shampoo frequency (water droplets)
    "sham-3": svg('<path d="M17 21c3 4 4 5.5 4 7.5a4 4 0 0 1-8 0c0-2 1-3.5 4-7.5z"/><path d="M31 15c3.5 5 5 6.8 5 9a5 5 0 0 1-10 0c0-2.2 1.5-4 5-9z"/><path d="M24 29c2.4 3.4 3.4 4.7 3.4 6.3a3.4 3.4 0 0 1-6.8 0c0-1.6 1-2.9 3.4-6.3z"/>'),
    "sham-2": svg('<path d="M19 17c4 5.5 5.5 7.6 5.5 10a5.5 5.5 0 0 1-11 0c0-2.4 1.5-4.5 5.5-10z"/><path d="M31 23c2.7 3.8 3.8 5.2 3.8 6.9a3.8 3.8 0 0 1-7.6 0c0-1.7 1.1-3.1 3.8-6.9z"/>'),
    "sham-1": svg('<path d="M24 13c5 7 7 9.6 7 13a7 7 0 0 1-14 0c0-3.4 2-6 7-13z"/>'),
    // cosmetic procedures (molecule / prohibited)
    "cosmetic-yes": svg('<polygon points="24,13 32,18 32,28 24,33 16,28 16,18"/><circle cx="24" cy="13" r="1.6"/><circle cx="32" cy="18" r="1.6"/><circle cx="32" cy="28" r="1.6"/><circle cx="24" cy="33" r="1.6"/><circle cx="16" cy="28" r="1.6"/><circle cx="16" cy="18" r="1.6"/>'),
    "cosmetic-no": svg('<circle cx="24" cy="24" r="12"/><line x1="15.5" y1="15.5" x2="32.5" y2="32.5"/>'),
    // stressful event (rain cloud / calm cloud)
    "stress-yes": svg('<path d="M17 28a6 6 0 0 1 .6-12 8 8 0 0 1 15 1.6A5 5 0 0 1 31 28z"/><line x1="19" y1="33" x2="18" y2="39"/><line x1="24" y1="33" x2="23" y2="39"/><line x1="29" y1="33" x2="28" y2="39"/>'),
    "stress-no": svg('<circle cx="32" cy="18" r="3.5"/><path d="M16 32a6 6 0 0 1 .6-12 8 8 0 0 1 15 1.6A5 5 0 0 1 30 32z"/>')
  };
  function illo(v) {
    if (!v) return "";
    if (v.indexOf("<svg") === 0 || v.indexOf("<") === 0) return v; // raw svg
    return ICONS[v] || "";
  }

  /* ---- tracking ---------------------------------------------------------- */
  function prefix() { var c = window.QUIZ_CONFIG || {}; return (c.tracking && c.tracking.eventPrefix) || "quiz"; }
  function sessionId() {
    try {
      var k = "quizSessionId", v = sessionStorage.getItem(k);
      if (!v) { v = "qs_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8); sessionStorage.setItem(k, v); }
      return v;
    } catch (e) { return "qs_anon"; }
  }
  function track(name, params) {
    var evt = prefix() + "_" + name;
    params = Object.assign({ quiz_session_id: sessionId() }, params || {});
    var payload = Object.assign({ event: evt, quiz_id: (window.QUIZ_CONFIG || {}).id || "quiz" }, params || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) {}
    try { if (typeof window.gtag === "function") window.gtag("event", evt, params || {}); } catch (e) {}
    try { if (typeof window.fbq === "function") window.fbq("trackCustom", evt, params || {}); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent("quiz:" + name, { detail: params || {} })); } catch (e) {}
    if ((window.QUIZ_CONFIG || {}).debug) console.log("[quiz]", evt, params || {});
  }
  window.QuizTrack = track;
  // Stage milestones (intro view / start / complete / results) must fire once
  // per quiz session. Re-renders, Back-to-landing and page reloads used to
  // re-fire them, inflating GA event counts and the Facebook pixel (both hang
  // off track()). The flag lives in sessionStorage, same lifecycle as
  // quizSessionId, so a genuinely new session re-arms every milestone.
  // Step-level events (step_view / answer) and offer_cta intentionally stay
  // multi-fire and keep using track() directly.
  function trackOnce(name, params) {
    try {
      var k = "quizFired:" + name;
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, "1");
    } catch (e) {}
    track(name, params);
  }
  window.QuizTrackOnce = trackOnce;

  /* ---- helpers ----------------------------------------------------------- */
  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { if (k === "class") n.className = attrs[k]; else if (attrs[k] != null) n.setAttribute(k, attrs[k]); });
    if (html != null) n.innerHTML = html;
    return n;
  }
  var CHECK = '<svg viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4.5" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var BACK_ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>';
  var STAR = '<svg class="q-star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z"/></svg>';
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]; }); }
  function titleHTML(s) { return esc(s).replace(/\[\[(.+?)\]\]/g, '<span class="q-hl">$1</span>'); } // [[word]] -> accent
  function brandEl(cfg) {
    if (cfg.logo) { var i = document.createElement("img"); i.className = "q-logo"; i.src = cfg.logo; i.alt = cfg.brand || ""; return i; }
    return el("div", { class: "q-brand" }, esc(cfg.brand || ""));
  }
  function saveResult(d) { try { sessionStorage.setItem(SS_KEY, JSON.stringify(d)); } catch (e) {} }
  function loadResult() { try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "null"); } catch (e) { return null; } }
  window.QuizResult = { save: saveResult, load: loadResult, KEY: SS_KEY };

  /* =========================================================================
     <quiz-funnel>
     ========================================================================= */
  class QuizFunnel extends HTMLElement {
    connectedCallback() {
      this.cfg = window.QUIZ_CONFIG;
      if (!this.cfg) { this.innerHTML = '<p style="padding:40px;text-align:center">Missing QUIZ_CONFIG.</p>'; return; }
      this.answers = {};
      this.scores = {};
      this.lastLeader = null;
      Object.keys(this.cfg.profiles || {}).forEach((k) => (this.scores[k] = 0));
      this.qCount = (this.cfg.questions || []).length;

      /* HTML-card mode: if <template data-card="..."> children exist, they ARE
         the quiz steps — one template per card, order = DOM order. Reorder the
         quiz by moving the template blocks in the page; no JS change needed.
         Scoring/factor data still comes from QUIZ_CONFIG.questions, looked up
         by data-card / data-option id (data-scores attr overrides per option). */
      var tpls = Array.prototype.slice.call(this.querySelectorAll("template[data-card]"));
      if (tpls.length) {
        var qById = {};
        (this.cfg.questions || []).forEach(function (q) { qById[q.id] = q; });
        this.steps = tpls.map(function (t) {
          var id = t.getAttribute("data-card");
          var q = qById[id] || null;
          // Conditional card: data-show-if="question_id:opt1|opt2" — the card
          // only appears if one of those answers was picked earlier.
          var showIf = null, si = t.getAttribute("data-show-if");
          if (si && si.indexOf(":") > -1) {
            var parts = si.split(":");
            showIf = { q: parts[0].trim(), ids: parts[1].split("|").map(function (x) { return x.trim(); }).filter(Boolean) };
          }
          return { id: id, tpl: t, q: q, showIf: showIf, type: t.getAttribute("data-type") || (q && q.type) || "single" };
        });
        this.qCount = this.steps.length;
      }

      // intro.mode "skip" opens straight on question 1 (no gate screen). The
      // control loses ~70% on the gate, so the no-gate variant has to be a real
      // option. quiz_view still fires on mount either way, so "landed on the
      // quiz" stays the same denominator across variants.
      var introMode = ((this.cfg.intro || {}).mode || "gate");
      // Lowest reachable index. In skip mode there is no gate to go back to, so
      // Back must stop at question 1 instead of painting an intro that this
      // variant does not have.
      this.minIdx = introMode === "skip" ? 0 : -1;
      this.idx = this.minIdx;
      this.render();
      trackOnce("view", { screen: introMode === "skip" ? "q1" : "intro" });
    }

    render() {
      this.innerHTML = "";
      var shell = el("div", { class: "q-shell" });
      shell.appendChild(el("div", { class: "q-bg", "aria-hidden": "true" }));

      // Promise bar. With no gate screen there is nowhere else to say what this
      // is or how long it takes, and every no-gate funnel we looked at keeps a
      // line like this above question 1.
      var bar = this.cfg.bar;
      if (bar && (bar.title || bar.sub)) {
        var pb = el("div", { class: "q-promobar" });
        if (bar.title) pb.appendChild(el("strong", { class: "q-promobar__t" }, esc(bar.title)));
        if (bar.sub) pb.appendChild(el("span", { class: "q-promobar__s" }, esc(bar.sub)));
        shell.appendChild(pb);
      }

      var top = el("div", { class: "q-topbar" });
      var inner = el("div", { class: "q-topbar__inner" });
      this.backBtn = el("button", { class: "q-back", "aria-label": "Back", hidden: "" }, '<span class="q-back__chip">' + BACK_ARROW + '</span><span class="q-back__txt">Back</span>');
      this.backBtn.addEventListener("click", () => this.back());
      inner.appendChild(this.backBtn);
      if (this.cfg.brand || this.cfg.logo) inner.appendChild(brandEl(this.cfg));
      this.counter = el("div", { class: "q-counter" }, "");
      inner.appendChild(this.counter);
      top.appendChild(inner);
      this.progressLine = el("div", { class: "q-progressline" });
      this.progressLine.innerHTML = '<div class="q-progressline__fill"></div>';
      top.appendChild(this.progressLine);
      shell.appendChild(top);

      this.main = el("div", { class: "q-main" });
      shell.appendChild(this.main);

      this.footer = el("div", { class: "q-footer", hidden: "" });
      this.footer.innerHTML = '<div class="q-footer__inner"></div>';
      shell.appendChild(this.footer);

      var skip = this.cfg.skipLink;
      if (skip && skip.href && skip.label) {
        var sk = el("div", { class: "q-skip" });
        var a = el("a", { class: "q-skip__a", href: skip.href }, esc(skip.label));
        a.addEventListener("click", function () { track("skip_quiz", {}); });
        sk.appendChild(a);
        shell.appendChild(sk);
      }

      this.appendChild(shell);
      this.paint();
    }

    /** Is a step eligible given the answers so far? (non-conditional: always) */
    eligible(st) {
      if (!st) return true;
      if (st.showIf) {
        var a = this.answers[st.showIf.q] || [];
        return st.showIf.ids.some(function (id) { return a.indexOf(id) > -1; });
      }
      /* Config-path conditions. { question, in:[...] } or { question, includes:[...] } */
      var c = st.condition;
      if (!c || !c.question) return true;
      var ans = this.answers[c.question] || [];
      var want = c.in || c.includes || [];
      if (!want.length) return true;
      return want.some(function (id) { return ans.indexOf(id) > -1; });
    }

    /* The active step list, whichever path is in use */
    stepList() { return this.steps || this.cfg.questions || []; }
    isInterstitial(st) { return !!st && st.type === "interstitial"; }

    setProgress() {
      var showing = this.idx >= 0 && this.idx < this.qCount;
      this.progressLine.style.visibility = showing ? "visible" : "hidden";
      var pos = this.idx + 1, total = this.qCount;
      if (showing) {
        // count only eligible, non-interstitial steps so skipped cards and
        // knowledge screens don't inflate "N of M"
        var self = this, list = this.stepList(); pos = 0; total = 0;
        list.forEach(function (st, j) {
          if (!self.eligible(st) || self.isInterstitial(st)) return;
          total += 1;
          if (j <= self.idx) pos += 1;
        });
        if (pos < 1) pos = 1;
      }
      this.counter.textContent = showing ? pos + " of " + total : "";
      if (showing) this.progressLine.querySelector(".q-progressline__fill").style.width = Math.round((pos / Math.max(1, total)) * 100) + "%";
    }

    back() {
      var floor = (this.minIdx == null ? -1 : this.minIdx);
      if (this.idx <= floor) return;
      var list = this.stepList();
      this.idx -= 1;
      while (this.idx > floor && !this.eligible(list[this.idx])) this.idx -= 1;
      this.paint();
    }

    paint() {
      /* is-landing switches on the full-bleed landing CSS (no topbar, no width
         cap). Only the heroImage intro renders that wrapper — the centered
         intro needs the normal column, or its CTA runs edge to edge. */
      this.classList.toggle("is-landing", this.idx === -1 && !!(this.cfg.intro || {}).heroImage);
      this.backBtn.hidden = this.idx <= (this.minIdx == null ? -1 : this.minIdx);
      this.setProgress();
      this.footer.hidden = true;
      this.footer.querySelector(".q-footer__inner").innerHTML = "";
      this.main.innerHTML = "";
      if (this.idx === -1) return this.paintIntro();
      if (this.idx >= this.qCount) return this.paintAnalyze();
      return this.paintQuestion(this.idx);
    }

    /* ---- intro (landing) ---- */
    startBlock(big) {
      var self = this, c = this.cfg.intro || {}, start = c.start || {};
      var frag = document.createDocumentFragment();
      if (start.choices && start.choices.length) {
        if (start.prompt) frag.appendChild(el("p", { class: "q-start__prompt" }, esc(start.prompt)));
        var wrap = el("div", { class: "q-starts" + (big ? " q-starts--lg" : "") });
        start.choices.forEach(function (ch) {
          var b = el("button", { class: "q-start", type: "button" });
          b.innerHTML = '<span class="q-start__icon">' + illo(ch.illustration) + "</span><span class='q-start__label'>" + esc(ch.label) + "</span>";
          b.addEventListener("click", function () {
            if (ch.scores) self.applyScores(ch.scores);
            self._segment = ch.segment || ch.id;
            trackOnce("start", { segment: self._segment });
            self.idx = 0; self.paint();
          });
          wrap.appendChild(b);
        });
        frag.appendChild(wrap);
      } else {
        var cta = el("button", { class: "q-cta", type: "button" }, esc(c.ctaText || start.ctaText || "Start"));
        cta.style.marginTop = "16px";
        cta.addEventListener("click", function () { trackOnce("start", {}); self.idx = 0; self.paint(); });
        frag.appendChild(cta);
        if (start.note) frag.appendChild(el("p", { class: "q-cta-note" }, esc(start.note)));
      }
      return frag;
    }

    benefitsGrid() {
      var c = this.cfg.intro || {};
      var grid = el("div", { class: "q-benefits" });
      (c.benefits || []).forEach(function (b) { grid.appendChild(el("div", { class: "q-benefit" }, '<span class="q-benefit__icon">' + illo(b.icon) + '</span><span class="q-benefit__label">' + esc(b.label) + "</span>")); });
      return grid;
    }

    paintIntro() {
      var c = this.cfg.intro || {}, self = this;
      var badgesEl = function () { var bg = el("div", { class: "q-badges" }); (c.badges || []).forEach(function (x) { bg.appendChild(el("span", { class: "q-badge" }, esc(x))); }); return bg; };
      var trustEl = function () { var t = el("div", { class: "q-trust" }); (c.trust || []).forEach(function (x) { t.appendChild(el("span", null, "&#10003; " + esc(x))); }); return t; };

      if (c.heroImage) {
        // ---- full-bleed landing ----
        var wrap = el("div", { class: "q-landing" });

        var nav = el("div", { class: "q-landing__nav" });
        if (this.cfg.brand || this.cfg.logo) nav.appendChild(brandEl(this.cfg));
        wrap.appendChild(nav);

        var hero = el("section", { class: "q-landing__hero" });
        var inner = el("div", { class: "q-landing__hero-inner" });
        var copy = el("div", { class: "q-hero-copy" });
        if (c.eyebrow) copy.appendChild(el("span", { class: "q-eyebrow-pill" }, esc(c.eyebrow)));
        if (c.title) { var h = el("h1", { class: "q-landing__h1" }); h.innerHTML = titleHTML(c.title); copy.appendChild(h); }
        if (c.sub) copy.appendChild(el("p", { class: "q-landing__sub" }, esc(c.sub)));
        // rating row — real numbers only (config), stars + count + trust line
        if (c.rating) {
          var rt = c.rating;
          copy.appendChild(el("div", { class: "q-rating" },
            '<span class="q-rating__stars" aria-hidden="true">' + STAR.repeat(5) + "</span>" +
            '<span class="q-rating__txt"><strong>' + esc(rt.score) + "</strong>" +
            (rt.count ? " · " + esc(rt.count) : "") + "</span>" +
            (rt.note ? '<span class="q-rating__note">' + esc(rt.note) + "</span>" : "")));
        }
        copy.appendChild(this.startBlock(true));
        if (c.trust && c.trust.length) copy.appendChild(trustEl());
        inner.appendChild(copy);
        var media = el("div", { class: "q-hero-media" });
        media.innerHTML = '<img class="q-hero-media__img" src="' + esc(c.heroImage) + '" alt="">';
        inner.appendChild(media);
        hero.appendChild(inner);
        wrap.appendChild(hero);

        if (c.benefits && c.benefits.length) {
          var band = el("section", { class: "q-landing__benefits" });
          var bi = el("div", { class: "q-landing__band-inner" });
          if (c.benefitsTitle) bi.appendChild(el("p", { class: "q-band-title" }, esc(c.benefitsTitle)));
          var grid = el("div", { class: "q-benefits q-benefits--row" });
          c.benefits.forEach(function (b) { grid.appendChild(el("div", { class: "q-benefit" }, '<span class="q-benefit__icon">' + illo(b.icon) + '</span><span class="q-benefit__label">' + esc(b.label) + "</span>")); });
          bi.appendChild(grid); band.appendChild(bi); wrap.appendChild(band);
        }

        var foot = el("section", { class: "q-landing__foot" });
        if (c.badges && c.badges.length) foot.appendChild(badgesEl());
        if (c.socialProof) foot.appendChild(el("p", { class: "q-social" }, esc(c.socialProof)));
        if (c.disclaimer) foot.appendChild(el("p", { class: "q-fineprint" }, esc(c.disclaimer)));
        wrap.appendChild(foot);

        this.main.appendChild(wrap);
        return;
      }

      // ---- simple centered fallback ----
      var s = el("div", { class: "q-screen q-intro" });
      if (c.eyebrow) s.appendChild(el("p", { class: "q-eyebrow" }, esc(c.eyebrow)));
      if (c.title) { var h2 = el("h1", { class: "q-title q-intro__title" }); h2.innerHTML = titleHTML(c.title); s.appendChild(h2); }
      if (c.sub) s.appendChild(el("p", { class: "q-sub" }, esc(c.sub)));
      if (c.benefits && c.benefits.length) s.appendChild(this.benefitsGrid());
      s.appendChild(this.startBlock());
      if (c.badges && c.badges.length) s.appendChild(badgesEl());
      if (c.socialProof) s.appendChild(el("p", { class: "q-social" }, esc(c.socialProof)));
      if (c.trust && c.trust.length) s.appendChild(trustEl());
      if (c.disclaimer) s.appendChild(el("p", { class: "q-fineprint" }, esc(c.disclaimer)));
      this.main.appendChild(s);
    }

    /* ---- question ---- */
    paintQuestion(i) {
      var stepHere = this.stepList()[i];
      if (this.isInterstitial(stepHere)) return this.paintInterstitial(stepHere, i);
      if (stepHere && stepHere.type === "email") return this.paintEmail(stepHere, i);
      if (this.steps) return this.paintCard(i);
      var q = this.cfg.questions[i];
      if (q.type === "slider") return this.paintSlider(q, i);
      var multi = q.type === "multi";
      /* Layout resolution. Explicit `layout` always wins; otherwise infer it
         from what the options actually carry, so a config that just adds
         `image` to every option gets photo rows without extra ceremony. */
      var layout = q.layout || (q.options.some((o) => o.image) ? "photo" : (q.options.some((o) => o.illustration) ? "cards" : "rows"));
      var asCards = layout === "cards";
      var asPhoto = layout === "photo";
      var asPortrait = layout === "portrait";
      var s = el("div", { class: "q-screen" });
      s.appendChild(el("h2", { class: "q-question" }, esc(q.text)));
      if (q.hint || multi) s.appendChild(el("p", { class: "q-hint" }, esc(q.hint || "Select all that apply")));

      var wrapClass = "q-options";
      if (asCards) wrapClass += " q-options--cards";
      else if (asPhoto) wrapClass += " q-options--photo";
      else if (asPortrait) wrapClass += " q-options--portrait";
      var wrap = el("div", { class: wrapClass });
      var current = this.answers[q.id] || [];
      q.options.forEach((opt) => {
        var selected = current.indexOf(opt.id) > -1;
        var b;
        if (asPhoto) {
          b = el("button", { class: "q-option q-option--photo" + (selected ? " is-selected" : ""), type: "button", "aria-pressed": selected ? "true" : "false" });
          b.innerHTML =
            '<span class="q-option__body"><span class="q-option__label">' + esc(opt.label) + "</span>" +
            (opt.desc ? '<span class="q-option__desc">' + esc(opt.desc) + "</span>" : "") + "</span>" +
            (opt.image ? '<img class="q-option__photo" src="' + esc(opt.image) + '" alt="" loading="lazy" decoding="async">' : "") +
            '<span class="q-option__check">' + CHECK + "</span>";
        } else if (asPortrait) {
          var cutout = (opt.imageFit || q.imageFit) === "contain";
          b = el("button", { class: "q-option q-option--portrait" + (cutout ? " is-cutout" : "") + (selected ? " is-selected" : ""), type: "button", "aria-pressed": selected ? "true" : "false" });
          b.innerHTML =
            (opt.image ? '<img class="q-option__portrait" src="' + esc(opt.image) + '" alt="" loading="lazy" decoding="async">' : "") +
            '<span class="q-option__caption"><span class="q-option__label">' + esc(opt.label) + "</span>" +
            (opt.desc ? '<span class="q-option__desc">' + esc(opt.desc) + "</span>" : "") + "</span>" +
            '<span class="q-option__check">' + CHECK + "</span>";
        } else if (asCards) {
          b = el("button", { class: "q-card" + (selected ? " is-selected" : ""), type: "button", "aria-pressed": selected ? "true" : "false" });
          b.innerHTML =
            '<span class="q-card__check">' + CHECK + "</span>" +
            (opt.illustration ? '<span class="q-illo">' + illo(opt.illustration) + "</span>" : (opt.emoji ? '<span class="q-illo q-illo--emoji">' + esc(opt.emoji) + "</span>" : "")) +
            '<span class="q-card__label">' + esc(opt.label) + "</span>" +
            (opt.desc ? '<span class="q-card__desc">' + esc(opt.desc) + "</span>" : "");
        } else {
          b = el("button", { class: "q-option" + (selected ? " is-selected" : ""), type: "button", "aria-pressed": selected ? "true" : "false" });
          b.innerHTML =
            (opt.emoji ? '<span class="q-option__emoji">' + esc(opt.emoji) + "</span>" : "") +
            '<span class="q-option__body"><span class="q-option__label">' + esc(opt.label) + "</span>" +
            (opt.desc ? '<span class="q-option__desc">' + esc(opt.desc) + "</span>" : "") + "</span>" +
            '<span class="q-option__check">' + CHECK + "</span>";
        }
        b.addEventListener("click", () => this.choose(q, opt, b, multi));
        wrap.appendChild(b);
      });
      s.appendChild(wrap);
      this.main.appendChild(s);
      window.scrollTo({ top: 0, behavior: "auto" });
      track("step_view", { step_index: i + 1, question_id: q.id, question_text: q.text });

      if (multi) {
        var cont = el("button", { class: "q-cta", type: "button" }, "Continue");
        cont.disabled = !(this.answers[q.id] && this.answers[q.id].length);
        cont.addEventListener("click", () => this.advanceFrom(q));
        this.footer.querySelector(".q-footer__inner").appendChild(cont);
        this.footer.hidden = false;
        this._multiContinue = cont;
      }
    }

    /* ---- HTML-card step: clone the <template>, hydrate behavior ---- */
    paintCard(i) {
      var self = this, st = this.steps[i];
      var multi = st.type === "multi";
      var s = el("div", { class: "q-screen q-screen--card" });
      s.appendChild(st.tpl.content.cloneNode(true));

      // Build the option lookup: config option (scores/factor) by id, with
      // HTML fallbacks so a card can exist without a config entry.
      var cfgOpts = (st.q && st.q.options) || [];
      var current = this.answers[st.id] || [];
      var opts = [];
      var optBtns = s.querySelectorAll("[data-option]");
      Array.prototype.forEach.call(optBtns, function (b) {
        var oid = b.getAttribute("data-option");
        var opt = cfgOpts.find(function (o) { return o.id === oid; });
        if (!opt) opt = { id: oid, label: (b.textContent || "").replace(/\s+/g, " ").trim(), scores: {} };
        var ds = b.getAttribute("data-scores");
        if (ds) { try { opt = Object.assign({}, opt, { scores: JSON.parse(ds) }); } catch (e) {} }
        opts.push(opt);
        var sel = current.indexOf(oid) > -1;
        b.classList.toggle("is-selected", sel);
        b.setAttribute("aria-pressed", sel ? "true" : "false");
        b.addEventListener("click", function () { self.choose(qLike, opt, b, multi); });
      });
      var qLike = st.q ? Object.assign({}, st.q, { options: opts }) : { id: st.id, options: opts };

      var cont = s.querySelector("[data-continue]");
      var sliderMount = s.querySelector("[data-slider]");

      if (multi && cont) {
        cont.disabled = !current.length;
        this._multiContinue = cont;
        cont.addEventListener("click", function () { self.advanceFrom(qLike); });
      }

      if (st.type === "slider" && sliderMount && st.q) {
        var w = this.sliderWidget(st.q);
        sliderMount.innerHTML = "";
        sliderMount.appendChild(w.box);
        if (cont) cont.addEventListener("click", function () { self.commitSlider(st.q, i, w.value()); });
      }

      this.main.appendChild(s);
      window.scrollTo({ top: 0, behavior: "auto" });
      var titleEl = s.querySelector(".q-question");
      track("step_view", { step_index: i + 1, question_id: st.id, question_text: titleEl ? titleEl.textContent.trim() : "" });
    }

    commitSlider(q, i, v) {
      this.answers[q.id] = [String(v)];
      this.applyScores(this.bucketScores(q, v));
      trackOnce("start", {}); track("answer", { step_index: i + 1, question_id: q.id, answer_value: v });
      this.advance();
    }

    /* ---- slider widget (shared by config + HTML-card modes) ---- */
    sliderWidget(q) {
      var min = q.min != null ? q.min : 0, max = q.max != null ? q.max : 100, step = q.step || 1;
      var prev = (this.answers[q.id] && this.answers[q.id][0] != null) ? +this.answers[q.id][0] : (q.default != null ? q.default : Math.round((min + max) / 2));
      var units = (q.units && q.units.length) ? q.units : [{ suffix: q.unit || "", factor: 1, dp: (step < 1 ? 1 : 0) }];
      var unitIdx = 0;

      var box = el("div", { class: "q-slider q-slider--rich" });
      box.innerHTML =
        (units.length > 1 ? '<div class="q-unit-toggle"></div>' : "") +
        '<div class="q-slider__row">' +
        (q.plusminus ? '<button class="q-slider__pm" data-pm="-" type="button" aria-label="Decrease">&minus;</button>' : "") +
        '<div class="q-slider__track"><span class="q-slider__bubble"></span>' +
        '<input class="q-range" type="range" min="' + min + '" max="' + max + '" step="' + step + '" value="' + prev + '" aria-label="' + esc(q.text) + '"></div>' +
        (q.plusminus ? '<button class="q-slider__pm" data-pm="+" type="button" aria-label="Increase">+</button>' : "") +
        "</div>" +
        (q.ticks ? '<div class="q-slider__ticks"></div>' : '<div class="q-slider__ends"><span>' + esc(q.minLabel != null ? q.minLabel : min) + "</span><span>" + esc(q.maxLabel != null ? q.maxLabel : max) + "</span></div>");

      var range = box.querySelector(".q-range"), bubble = box.querySelector(".q-slider__bubble");
      var ticksEl = box.querySelector(".q-slider__ticks"), toggleEl = box.querySelector(".q-unit-toggle");
      var pctOf = function (v) { return ((v - min) / (max - min)) * 100; };
      var fmt = function (base, noSuffix) { var u = units[unitIdx], v = base * (u.factor || 1), dp = u.dp != null ? u.dp : (step < 1 ? 1 : 0); return (dp ? v.toFixed(dp) : String(Math.round(v))) + (!noSuffix && u.suffix ? " " + u.suffix : ""); };
      var place = function () { var p = pctOf(+range.value); range.style.setProperty("--pct", p + "%"); bubble.textContent = fmt(+range.value); bubble.style.left = p + "%"; };
      var renderTicks = function () { if (!ticksEl) return; var h = ""; for (var v = min; v <= max + 1e-9; v += step) h += '<span class="q-tick" style="left:' + pctOf(v) + '%"><span class="q-tick__mark"></span><span class="q-tick__lbl">' + esc(fmt(v, true)) + "</span></span>"; ticksEl.innerHTML = h; };
      var renderToggle = function () {
        if (!toggleEl) return;
        toggleEl.innerHTML = units.map(function (u, idx) { return '<button type="button" class="q-unit' + (idx === unitIdx ? " is-active" : "") + '" data-uidx="' + idx + '">' + esc(u.label) + "</button>"; }).join("");
        toggleEl.querySelectorAll(".q-unit").forEach(function (b) { b.addEventListener("click", function () { unitIdx = +b.dataset.uidx; renderToggle(); renderTicks(); place(); }); });
      };
      place(); renderTicks(); renderToggle();
      range.addEventListener("input", place);
      box.querySelectorAll(".q-slider__pm").forEach(function (b) { b.addEventListener("click", function () { var d = b.dataset.pm === "+" ? step : -step; range.value = Math.min(max, Math.max(min, +range.value + d)); place(); }); });

      return { box: box, value: function () { return +range.value; } };
    }

    /* ---- slider question, config-rendered mode ---- */
    paintSlider(q, i) {
      var self = this;
      var s = el("div", { class: "q-screen" });
      s.appendChild(el("h2", { class: "q-question" }, esc(q.text)));
      if (q.hint) s.appendChild(el("p", { class: "q-hint" }, esc(q.hint)));
      var w = this.sliderWidget(q);
      s.appendChild(w.box);
      this.main.appendChild(s);
      window.scrollTo({ top: 0, behavior: "auto" });

      track("step_view", { step_index: i + 1, question_id: q.id, question_text: q.text });
      var cont = el("button", { class: "q-cta", type: "button" }, q.ctaText || "Continue");
      cont.addEventListener("click", function () { self.commitSlider(q, i, w.value()); });
      this.footer.querySelector(".q-footer__inner").appendChild(cont);
      this.footer.hidden = false;
    }

    bucketScores(q, v) {
      if (!q.buckets || !q.buckets.length) return null;
      for (var b = 0; b < q.buckets.length; b++) { var bk = q.buckets[b]; if (bk.upTo == null || v <= bk.upTo) return bk.scores; }
      return q.buckets[q.buckets.length - 1].scores;
    }

    choose(q, opt, btn, multi) {
      this.answers[q.id] = this.answers[q.id] || [];
      var selClass = btn.classList.contains("q-card") ? "is-selected" : "is-selected";
      if (multi) {
        var pos = this.answers[q.id].indexOf(opt.id);
        if (pos > -1) { this.answers[q.id].splice(pos, 1); btn.classList.remove(selClass); btn.setAttribute("aria-pressed", "false"); }
        else { this.answers[q.id].push(opt.id); btn.classList.add(selClass); btn.setAttribute("aria-pressed", "true"); }
        if (this._multiContinue) this._multiContinue.disabled = !this.answers[q.id].length;
        trackOnce("start", {}); track("answer", { step_index: this.idx + 1, question_id: q.id, answer_id: opt.id, answer_text: opt.label, multi: true });
      } else {
        this.answers[q.id] = [opt.id];
        this.applyScores(opt.scores);
        trackOnce("start", {}); track("answer", { step_index: this.idx + 1, question_id: q.id, answer_id: opt.id, answer_text: opt.label });
        btn.classList.add("is-selected");
        setTimeout(() => this.advance(), 160); // brief highlight before advancing
      }
    }

    advanceFrom(q) {
      (this.answers[q.id] || []).forEach((oid) => { var opt = q.options.find((o) => o.id === oid); if (opt) this.applyScores(opt.scores); });
      this.advance();
    }

    applyScores(scores) {
      if (!scores) return;
      Object.keys(scores).forEach((k) => { this.scores[k] = (this.scores[k] || 0) + scores[k]; if (this.scores[k] >= (this.scores[this.lastLeader] || 0)) this.lastLeader = k; });
    }

    advance() {
      var list = this.stepList();
      this.idx += 1;
      while (this.idx < list.length && !this.eligible(list[this.idx])) this.idx += 1;
      this.paint();
    }

    /* ---- Email capture step ----------------------------------------
       Optional by default. The result is NEVER gated behind it: Continue
       stays enabled with the field empty.                                 */
    paintEmail(q, i) {
      var self = this;
      var s = el("div", { class: "q-screen q-screen--email" });
      s.appendChild(el("h2", { class: "q-question" }, esc(q.text)));
      if (q.hint) s.appendChild(el("p", { class: "q-hint" }, esc(q.hint)));
      var input = el("input", {
        class: "q-email__input", type: "email", inputmode: "email",
        autocomplete: "email", placeholder: q.placeholder || "you@example.com",
        "aria-label": esc(q.text)
      });
      if (this.answers[q.id] && this.answers[q.id].length) input.value = this.answers[q.id][0];
      s.appendChild(input);
      this.main.appendChild(s);
      window.scrollTo({ top: 0, behavior: "auto" });
      track("step_view", { step_index: i + 1, question_id: q.id, question_text: q.text });

      var cont = el("button", { class: "q-cta", type: "button" }, q.ctaText || "See my result");
      cont.addEventListener("click", function () {
        var v = (input.value || "").trim();
        if (v) {
          self.answers[q.id] = [v];
          track("answer", { step_index: i + 1, question_id: q.id, answer_value: "provided" });
        }
        self.advance();
      });
      this.footer.querySelector(".q-footer__inner").appendChild(cont);
      this.footer.hidden = false;
    }

    /* ---- Interstitial knowledge screen -------------------------------
       Renders a title/body/stat/quote card with a Continue button. Records no
       answer, applies no scores, and is excluded from the progress count.  */
    paintInterstitial(st, i) {
      var s = el("div", { class: "q-screen q-screen--interstitial" });
      if (st.eyebrow) s.appendChild(el("p", { class: "q-inter__eyebrow" }, esc(st.eyebrow)));
      if (st.title) s.appendChild(el("h2", { class: "q-question q-inter__title" }, esc(st.title)));
      if (st.body) s.appendChild(el("p", { class: "q-inter__body" }, esc(st.body)));
      if (st.stat) s.appendChild(el("p", { class: "q-inter__stat" }, esc(st.stat)));
      if (st.quote) s.appendChild(el("blockquote", { class: "q-inter__quote" }, esc(st.quote)));
      this.main.appendChild(s);
      window.scrollTo({ top: 0, behavior: "auto" });
      track("interstitial_view", { step_index: i + 1, question_id: st.id, question_text: st.title || "" });

      var cont = el("button", { class: "q-cta", type: "button" }, st.ctaText || "Continue");
      cont.addEventListener("click", () => this.advance());
      this.footer.querySelector(".q-footer__inner").appendChild(cont);
      this.footer.hidden = false;
    }

    paintAnalyze() {
      this.footer.hidden = true;
      var a = this.cfg.analyze || {};
      var steps = a.steps || ["Analyzing your answers", "Matching your profile", "Building your plan"];
      var total = a.durationMs || 2600;
      var s = el("div", { class: "q-screen q-analyze" });
      s.appendChild(el("div", { class: "q-spinner" }));
      s.appendChild(el("h2", { class: "q-title" }, esc(a.title || "Building your personalized plan")));
      var list = el("div", null, "");
      steps.forEach((txt) => list.appendChild(el("p", { class: "q-analyze__step" }, esc(txt))));
      s.appendChild(list);
      this.main.appendChild(s);

      var nodes = list.querySelectorAll(".q-analyze__step");
      var per = total / steps.length;
      nodes.forEach((node, i) => {
        setTimeout(() => { nodes.forEach((n2, j) => { if (j < i) { n2.classList.add("is-done"); n2.classList.remove("is-active"); } }); node.classList.add("is-active"); }, per * i);
        setTimeout(() => { node.classList.add("is-done"); node.innerHTML = '<span class="tick">&#10003;</span> ' + node.textContent; }, per * (i + 1) - 120);
      });

      var result = this.compute();
      saveResult(result);
      trackOnce("complete", { winning_profile: result.profileKey, total_answers: Object.keys(this.answers).length });
      setTimeout(() => { window.location.href = this.cfg.resultsUrl || "results.html"; }, total + 250);
    }

    compute() {
      var keys = Object.keys(this.scores), best = keys[0], max = -Infinity;
      keys.forEach((k) => { if (this.scores[k] > max) { max = this.scores[k]; best = k; } });
      if (this.lastLeader && this.scores[this.lastLeader] === max) best = this.lastLeader;
      return { profileKey: best, scores: this.scores, answers: this.answers, segment: this._segment || null, ts: Date.now() };
    }
  }

  /* =========================================================================
     <quiz-results> — TEMPLATE-DRIVEN.
     No markup lives here. The page (results.html) owns every element; this
     class only resolves data-* slots against the data:

       data-text / data-src / data-if / data-each / data-icon / data-bar-fill
       data-cta   -> wired to the checkout hand-off
       data-slot  -> mount point for the generated SVG charts (data-viz only)

     Data roots: cfg, results, profile, answers, computed.
     ========================================================================= */

  /** Resolve "a.b.c" against a context. "." is the item itself; a leading
   *  "$." escapes to the page root (used inside data-each templates). */
  function pick(ctx, root, path) {
    if (!path) return undefined;
    if (path === ".") return ctx;
    var src = ctx, key = path;
    if (path.indexOf("$.") === 0) { src = root; key = path.slice(2); }
    return key.split(".").reduce(function (o, k) { return (o == null ? o : o[k]); }, src);
  }
  function isEmpty(v) { return v == null || v === "" || v === false || (Array.isArray(v) && !v.length); }

  /** Fill every data-* slot inside `root` from `ctx` (page root = `page`). */
  function hydrate(root, ctx, page) {
    page = page || ctx;

    // data-if: drop the node entirely when the value is empty
    Array.prototype.slice.call(root.querySelectorAll("[data-if]")).forEach(function (n) {
      if (isEmpty(pick(ctx, page, n.getAttribute("data-if")))) n.remove();
    });

    // data-each: clone the inner <template> once per item, hydrate per item
    Array.prototype.slice.call(root.querySelectorAll("[data-each]")).forEach(function (n) {
      var items = pick(ctx, page, n.getAttribute("data-each"));
      var tpl = n.querySelector("template");
      if (!tpl) return;
      tpl.remove();
      if (!Array.isArray(items)) return;
      items.forEach(function (item) {
        var frag = tpl.content.cloneNode(true);
        hydrate(frag, item, page);
        n.appendChild(frag);
      });
    });

    Array.prototype.slice.call(root.querySelectorAll("[data-text]")).forEach(function (n) {
      var v = pick(ctx, page, n.getAttribute("data-text"));
      if (!isEmpty(v)) n.textContent = String(v);
      n.removeAttribute("data-text");
    });

    Array.prototype.slice.call(root.querySelectorAll("[data-src]")).forEach(function (n) {
      var v = pick(ctx, page, n.getAttribute("data-src"));
      if (isEmpty(v)) n.remove(); else n.setAttribute("src", String(v));
      n.removeAttribute("data-src");
    });

    Array.prototype.slice.call(root.querySelectorAll("[data-icon]")).forEach(function (n) {
      n.innerHTML = illo(pick(ctx, page, n.getAttribute("data-icon")));
      n.removeAttribute("data-icon");
    });

    Array.prototype.slice.call(root.querySelectorAll("[data-bar-fill]")).forEach(function (n) {
      var pct = +pick(ctx, page, n.getAttribute("data-bar-fill")) || 0;
      setTimeout(function () { n.style.width = pct + "%"; }, 150);
      n.removeAttribute("data-bar-fill");
    });
  }

  class QuizResults extends HTMLElement {
    connectedCallback() {
      this.cfg = window.QUIZ_CONFIG || {};
      var res = loadResult();
      var ok = res && this.cfg.profiles && this.cfg.profiles[res.profileKey];
      if (!ok) return this.paintEmpty();
      this.res = res;
      this.profile = this.cfg.profiles[res.profileKey];
      this.render();
      trackOnce("results_view", { winning_profile: res.profileKey });
    }

    /** No result in the session: swap in the page's <template data-empty>. */
    paintEmpty() {
      var tpl = this.querySelector("template[data-empty]");
      if (!tpl) return;
      var frag = tpl.content.cloneNode(true);
      var link = frag.querySelector("[data-empty-link]");
      if (link) link.setAttribute("href", this.cfg.quizUrl || "index.html");
      this.innerHTML = "";
      this.appendChild(frag);
    }

    /** Everything the page can bind to. Derived values are precomputed here so
     *  the HTML never has to do logic. */
    buildData() {
      var self = this, p = this.profile, r = this.cfg.results || {};
      var now = new Date();
      var fmtD = function (d) { return d.getDate() + " " + d.toLocaleString("en-US", { month: "short" }); };
      var plusM = function (m) { var d = new Date(now.getTime()); d.setMonth(d.getMonth() + m); return d; };
      var planMonths = (p.condition && p.condition.months) || (r.growth && r.growth.months) || 3;

      // factors: pulled from the options the user actually picked
      var factors = [];
      (this.cfg.questions || []).forEach(function (q) {
        var a = self.res.answers[q.id]; if (!a) return;
        (q.options || []).forEach(function (o) { if (a.indexOf(o.id) > -1 && o.factor) factors.push(o.factor); });
      });

      // profile match: share of the winning profile's score
      var sum = 0, scores = this.res.scores || {};
      Object.keys(scores).forEach(function (k) { sum += Math.max(0, scores[k]); });
      var win = Math.max(0, scores[this.res.profileKey] || 0);
      var match = sum ? Math.min(96, Math.round(55 + (win / sum) * 40)) : 72;

      // growth window scaled to the length the user asked for
      var goalIn = (this.res.answers.growth_goal && +this.res.answers.growth_goal[0]) || null;
      var growthMonths = goalIn ? Math.min(12, Math.max(2, Math.round(goalIn / 0.5))) : planMonths;
      var ticks = []; for (var t = 0; t <= growthMonths; t++) ticks.push(fmtD(plusM(t)));
      var textFrom = function (v) {
        if (v == null) return "";
        if (typeof v !== "object") return v;
        return v.text || v.label || v.name || v.title || v.value || "";
      };
      var reco = Object.assign({}, p.reco || {}, {
        includes: ((p.reco && p.reco.includes) || []).map(textFrom).filter(function (v) { return !isEmpty(v); })
      });

      return {
        cfg: this.cfg,
        results: r,
        answers: this.res.answers,
        profile: Object.assign({}, p, {
          reco: reco,
          bars: (p.bars || []).map(function (b) { return Object.assign({ pctLabel: b.pct + "%" }, b); })
        }),
        computed: {
          /* The add-on is conditional on purpose. It renders only when one
             of results.addOn.showForScores actually scored, so it is never
             bolted onto everyone. Showing a paid add-on to every visitor
             dilutes the "this was picked for you" promise the rest of the
             page just made, and in our tests it lost more revenue per
             visitor than the add-on brought in. */
          showAddOn: (function () {
            var ao = r.addOn, sc = self.res.scores || {};
            if (!ao || !ao.showForScores) return false;
            return ao.showForScores.some(function (k) { return (sc[k] || 0) > 0; });
          })(),
          factors: factors.slice(0, 4),
          primaryConcern: (p.bars && p.bars[0]) ? p.bars[0].label : "—",
          runwayLabel: planMonths + " months",
          match: match,
          expectSteps: ((r.expect && r.expect.steps) || []).map(function (s) {
            return Object.assign({ dateLabel: "by " + fmtD(plusM(s.month)) }, s);
          }),
          growth: Object.assign({}, r.growth || {}, {
            months: growthMonths, perMonth: 0.5,
            startLabel: fmtD(now), endLabel: fmtD(plusM(growthMonths)), tickLabels: ticks
          }),
          condition: Object.assign({}, p.condition || {}, {
            nowLabel: "Today · " + fmtD(now), futureLabel: fmtD(plusM(planMonths))
          })
        }
      };
    }

    render() {
      var self = this, data = this.buildData();

      hydrate(this, data, data);

      // CTAs: label fallback + the checkout hand-off
      Array.prototype.slice.call(this.querySelectorAll("[data-cta]")).forEach(function (b) {
        if (!b.textContent.trim()) b.textContent = (data.results.ctaText || "See my plan & offer");
        b.addEventListener("click", function () { self.goToOffer(); });
      });

      // charts mount into their slots (SVG data-viz — generated, by nature)
      var g = this.querySelector('[data-slot="growth-chart"]');
      if (g) g.appendChild(lineChart(data.computed.growth));
      var gg = this.querySelector('[data-slot="match-gauge"]');
      if (gg) gg.appendChild(gauge(data.computed.match, data.results.gaugeLabel || "match confidence"));
      var pj = this.querySelector('[data-slot="plan-journey"]');
      if (pj) pj.appendChild(journey(data.computed.condition));

      // sticky bar: reveal once the hero has scrolled away
      var sticky = this.querySelector("[data-sticky]");
      if (sticky) {
        var onScroll = function () { sticky.classList.toggle("is-visible", window.scrollY > 420); };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
      }
    }

    goToOffer() {
      var p = this.profile, pkg = p.reco && p.reco.packageId;
      track("offer_cta", { winning_profile: this.res.profileKey, package_id: pkg || null });
      var direct = this.cfg.checkoutUrl;
      if (direct && pkg) {
        var go = function () { window.location.href = direct + (direct.indexOf("?") > -1 ? "&" : "?") + "pkg=" + encodeURIComponent(pkg); };
        try {
          /* Optional host-cart hook: if the page's commerce platform exposes
             window.next.swapCart(), fill the cart before leaving. Harmless
             when absent — the redirect below runs either way. */
          if (window.next && typeof window.next.swapCart === "function") {
            Promise.resolve(window.next.swapCart([{ packageId: pkg, quantity: 1 }])).then(go, go);
            return;
          }
        } catch (e) {}
        return go();
      }
      var url = this.cfg.offerUrl || "offer.html";
      window.location.href = pkg ? (url + (url.indexOf("?") > -1 ? "&" : "?") + "pkg=" + encodeURIComponent(pkg)) : url;
    }
  }

  /* ---- charts: SVG is generated because it IS data (coordinates), but the
     card, title and layout around each chart live in the HTML. ------------- */
  function svgEl(tag, attrs) {
    var n = document.createElementNS("http://www.w3.org/2000/svg", tag);
    Object.keys(attrs || {}).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }
  function lineChart(g) {
    var months = g.months || 6, per = g.perMonth != null ? g.perMonth : 0.5, unit = g.unit || "in";
    var vals = []; for (var k = 0; k <= months; k++) vals.push(+(per * k).toFixed(1));
    var W = 340, H = 172, pl = 12, pr = 16, pt = 30, pb = 26, n = vals.length, maxV = vals[n - 1] || 1;
    var X = function (i) { return pl + i * ((W - pl - pr) / (n - 1)); };
    var Y = function (v) { return pt + (1 - (maxV ? v / maxV : 0)) * (H - pt - pb); };
    var svg = svgEl("svg", { class: "q-linechart", viewBox: "0 0 " + W + " " + H });
    for (var i = 0; i < n; i++) svg.appendChild(svgEl("line", { class: "grid", x1: X(i), y1: pt, x2: X(i), y2: H - pb }));
    var pts = vals.map(function (v, i2) { return X(i2) + "," + Y(v); });
    svg.appendChild(svgEl("path", { class: "area", d: "M" + X(0) + "," + (H - pb) + " L" + pts.join(" L") + " L" + X(n - 1) + "," + (H - pb) + " Z" }));
    svg.appendChild(svgEl("path", { class: "line", d: "M" + pts.join(" L") }));
    vals.forEach(function (v, i2) { svg.appendChild(svgEl("circle", { class: "dot", cx: X(i2), cy: Y(v), r: 3 })); });
    for (var j = 1; j < n; j++) {
      var mx = (X(j - 1) + X(j)) / 2, my = (Y(vals[j - 1]) + Y(vals[j])) / 2 - 12;
      svg.appendChild(svgEl("rect", { class: "pill", x: mx - 17, y: my - 8, width: 34, height: 15, rx: 7 }));
      var pt2 = svgEl("text", { class: "pill-text", x: mx, y: my + 2, "text-anchor": "middle" });
      pt2.textContent = "+" + per; svg.appendChild(pt2);
    }
    var lbl = svgEl("text", { class: "axis-text", x: pl, y: 16 }); lbl.textContent = "Projected length"; svg.appendChild(lbl);
    var big = svgEl("text", { class: "big", x: W - pr, y: 16, "text-anchor": "end" }); big.textContent = "+" + maxV + " " + unit; svg.appendChild(big);
    (g.tickLabels || []).forEach(function (t, i2) {
      if (i2 >= n) return;
      var a = i2 === 0 ? "start" : (i2 === n - 1 ? "end" : "middle");
      var tx = svgEl("text", { class: "axis-text", x: X(i2), y: H - 5, "text-anchor": a });
      tx.textContent = t; svg.appendChild(tx);
    });
    return svg;
  }
  function gauge(pct, label) {
    var wrap = el("div", { class: "q-gauge" });
    var len = Math.PI * 88, fill = Math.max(0, Math.min(100, pct)) / 100 * len;
    var svg = svgEl("svg", { viewBox: "0 0 200 118" });
    svg.appendChild(svgEl("path", { class: "q-gauge__track", d: "M12 106 A88 88 0 0 1 188 106", "stroke-width": 14 }));
    svg.appendChild(svgEl("path", { class: "q-gauge__fill", d: "M12 106 A88 88 0 0 1 188 106", "stroke-width": 14, "stroke-dasharray": fill + " 999" }));
    wrap.appendChild(svg);
    var val = el("div", { class: "q-gauge__val" });
    val.appendChild(el("div", { class: "q-gauge__num" }, esc(pct + "%")));
    val.appendChild(el("div", { class: "q-gauge__cap" }, esc(label)));
    wrap.appendChild(val);
    return wrap;
  }
  function journey(c) {
    var wrap = document.createDocumentFragment();
    var row = function (lbl, pct) {
      var d = el("div", { class: "q-cond" });
      d.appendChild(el("div", { class: "q-cond__label" }, "<span>" + esc(lbl) + "</span>"));
      var bar = el("div", { class: "q-cond__bar" });
      var m = el("span", { class: "q-cond__marker" });
      m.style.left = pct + "%";
      bar.appendChild(m); d.appendChild(bar);
      return d;
    };
    wrap.appendChild(row(c.nowLabel || "Today", c.nowPct != null ? c.nowPct : 25));
    wrap.appendChild(row(c.futureLabel || "With your plan", c.futurePct != null ? c.futurePct : 82));
    wrap.appendChild(el("div", { class: "q-cond__ends" },
      "<span>" + esc(c.leftLabel || "Needs work") + "</span><span>" + esc(c.rightLabel || "Healthy") + "</span>"));
    return wrap;
  }

  customElements.define("quiz-funnel", QuizFunnel);
  customElements.define("quiz-results", QuizResults);
})();
